import { Video } from '../types/Video';

/**
 * Shared playback utilities — single source of truth for "is a video playable?"
 * and related helpers. Used by useVideoSelection, Project.tsx, and VideoPlayer.
 */

/**
 * Determines if a video is playable (upload complete and is video/photo).
 * Text and music items are not playable. Uploading items are not playable.
 */
export function isVideoPlayable(video: Video): boolean {
  return (
    (!video.uploadState || video.uploadState === 'complete') &&
    (!video.media_type || video.media_type === 'video' || video.media_type === 'photo')
  );
}

/**
 * Filters an array to only playable videos/photos.
 */
export function getPlayableVideos(videos: Video[]): Video[] {
  return videos.filter(isVideoPlayable);
}

/**
 * Finds the nearest playable video starting at `fromIndex`, searching forward
 * first, then backward. Returns null if no playable video exists.
 */
export function findNearestPlayable(videos: Video[], fromIndex: number): Video | null {
  // Clamp to valid range
  const start = Math.max(0, Math.min(fromIndex, videos.length - 1));

  // Search forward
  for (let i = start; i < videos.length; i++) {
    if (isVideoPlayable(videos[i])) return videos[i];
  }
  // Search backward
  for (let i = start - 1; i >= 0; i--) {
    if (isVideoPlayable(videos[i])) return videos[i];
  }
  return null;
}

/**
 * Returns the first playable video in the array (earliest in position order).
 */
export function findEarliestPlayable(videos: Video[]): Video | null {
  for (const video of videos) {
    if (isVideoPlayable(video)) return video;
  }
  return null;
}

/**
 * Get the playback duration of a timeline item in seconds.
 *
 * For playable items (video/photo): returns croppedDuration (the actual
 * fMP4 file duration stored after transcoding). Throws if missing — a
 * silent fallback to `duration` or `2` hides bugs and causes cumulative
 * drift in large projects (200+ clips).
 *
 * For overlay items (text/music): returns the overlay display duration.
 * These don't advance the timeline — they overlay it.
 */
export function getItemPlaybackDuration(item: Video): number {
  const isPlayable = !item.media_type || item.media_type === 'video' || item.media_type === 'photo';

  if (isPlayable) {
    if (!item.croppedDuration) {
      console.error(`[PlaybackDuration] Missing croppedDuration for ${item.media_type || 'video'} "${item.name}" (${item.id}). Using duration fallback.`);
      return item.duration || 2;
    }
    return item.croppedDuration;
  }

  // Text overlays use text_duration field
  if (item.media_type === 'text') {
    return item.text_duration || item.duration || 0;
  }

  // Music overlays use duration (how long the track plays)
  return item.duration || 0;
}

/** A position on the playable timeline, expressed as a clip plus an offset into it. */
export interface TimelineSeekTarget {
  videoId: string;
  /** Seconds into that clip. */
  timeOffset: number;
  /** Index of the clip in `sortedVideos` (handy for logging). */
  index: number;
}

/**
 * Convert an absolute time on the playable timeline into the clip that contains
 * it plus the offset within that clip — the (videoId, timeOffset) pair every
 * seek entry point needs.
 *
 * Out-of-range times are clamped to the timeline: negatives land at the very
 * start, times past the end land at the end of the last clip. So callers can
 * pass an arbitrary time (e.g. currentTime ± 10s) without bounds-checking.
 *
 * Returns null only when there are no clips to seek within.
 */
export function resolveTimelineSeekTarget(
  sortedVideos: Video[],
  targetTime: number
): TimelineSeekTarget | null {
  if (sortedVideos.length === 0) return null;

  const clamped = Math.max(0, targetTime);
  let accumulatedTime = 0;

  for (let i = 0; i < sortedVideos.length; i++) {
    const video = sortedVideos[i];
    const videoEnd = accumulatedTime + getItemPlaybackDuration(video);
    if (clamped <= videoEnd) {
      return { videoId: video.id, timeOffset: clamped - accumulatedTime, index: i };
    }
    accumulatedTime = videoEnd;
  }

  // Past the end — land at the end of the last clip.
  const lastIndex = sortedVideos.length - 1;
  const last = sortedVideos[lastIndex];
  return {
    videoId: last.id,
    timeOffset: getItemPlaybackDuration(last),
    index: lastIndex,
  };
}

/**
 * Inverse of `resolveTimelineSeekTarget`: given a clip and an offset into it,
 * return the absolute time on the playable timeline.
 *
 * Returns the bare offset if the clip isn't in the list, so a caller logging
 * this value can't throw on a stale id.
 */
export function globalTimeAtClipOffset(
  sortedVideos: Video[],
  videoId: string,
  timeOffset: number
): number {
  let accumulatedTime = 0;
  for (const video of sortedVideos) {
    if (video.id === videoId) return accumulatedTime + timeOffset;
    accumulatedTime += getItemPlaybackDuration(video);
  }
  return timeOffset;
}
